import type { BaseQueryApi, BaseQueryFn, FetchArgs } from '@reduxjs/toolkit/query/react';
import { createApi } from '@reduxjs/toolkit/query/react';

import type { CustomPropertyGroup, CustomProperty } from '../features/properties/propertiesApi';
export * as default from './types';

const SESSION_TOKEN_KEY = 'session_token';

export const retrieveSessionTokenFromSessionStorage = (): string | null => {
  return window.sessionStorage.getItem(SESSION_TOKEN_KEY);
};
export const storeSessionTokenInSession = (token: string) => window.sessionStorage.setItem(SESSION_TOKEN_KEY, token);

// Define the base URL for the API based on environment. See setupProxy.js to see how "/api" url is used.
const baseUrl = process.env.NODE_ENV === 'production' ? process.env.REACT_APP_API_URL : '/api';

function logRequest(status: string | number, method: string, url: string) {
  if (!status || !method || !url) {
    console.error(
      `Invalid request data: ${status ? status : 'missing status'} ${method ? method : 'missing method'} ${
        url ? url : 'missing url'
      }`,
    );
    throw new Error('Invalid request data');
  }
  console.log(`Request made: ${status} ${method} ${url}`);
}

// customBaseQuery is used to log all requests. See https://redux-toolkit.js.org/rtk-query/usage/customizing-queries
const customBaseQuery: BaseQueryFn = async (args: FetchArgs, { getState }: BaseQueryApi) => {
  try {
    // args can be a string or an object with url, method, body, and headers
    let url = `${baseUrl}${args.url || args}`;

    // Handle query parameters
    if (args.params) {
      const query = new URLSearchParams();
      Object.entries(args.params).forEach(([key, value]) => {
        if (value) query.append(key, value);
      });
      url += `?${query.toString()}`;
    }

    const token = retrieveSessionTokenFromSessionStorage();

    const headers: any = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    else headers.Authorization = Object.entries(args.headers || {}).find(([key]) => key === 'Authorization')?.[1];

    const response = await fetch(url, {
      method: typeof args.method === 'string' ? args.method : 'GET',
      body: args.body ? JSON.stringify(args.body) : undefined,
      headers: {
        ...headers,
        'Content-Type': 'application/json',
      },
    });

    // logRequest(response.status, args.method || 'GET', url);

    // If response is JSON, await response.json() and return either the data or the error
    if (response.headers.get('content-type')?.includes('application/json')) {
      const data = await response.json();
      if (response.ok) {
        return { data };
      }
      return { error: { status: response.status, message: data.message ? data.message : data, data } };
    }

    // If response is not JSON, return the response text as either data or error
    const text = await response.text();
    if (response.ok) {
      return { data: text };
    }
    return { error: { status: response.status, message: text } };
  } catch (error) {
    return { error };
  }
};

// const defaultBaseQuery = fetchBaseQuery({
//   baseUrl,
//   credentials: 'include',
//   prepareHeaders: (headers, { getState }) => {
//     const state = getState() as RootState;
//     const token = state.auth.token;

//     if (token) headers.set('Authorization', `Bearer ${token}`);
//     return headers;
//   },
// });

export interface DebugInfoResponse {
  result: {
    objectId: string;
    objectType: string;
    properties: CustomProperty[];
    groups: CustomPropertyGroup[];
    grantedScopes: string[];
  };
}

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: customBaseQuery,
  tagTypes: [
    'ApiKey',
    'ApiKeyValidity',
    'OAuthScopesValidity',
    'AuthorizationUrl',
    'Properties',
    'Template',
    'Templates',
    'Object',
    'Objects',
    'ObjectTypes',
    'CustomObjectTypes',
    'ObjectType',
    'Settings',
  ],
  endpoints: (builder) => ({
    // DEBUG
    getDebugInfo: builder.query<DebugInfoResponse['result'], void>({
      query: () => '/hubspot/debug-info',
      transformResponse: (response: DebugInfoResponse) => response.result,
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const { useGetDebugInfoQuery, useLazyGetDebugInfoQuery } = apiSlice;
